import React, { useState } from 'react';
import Form from 'react-bootstrap/Form';
import { Send, Mail } from 'react-feather';
import { contactSupport } from '../../api';
import JumbleModal from '../../components/jumbleModal/JumbleModal';
import { useGeneralContext } from '../../context';
import { ReactComponent as WhatsappIcon } from '../../resources/WhatsApp.svg';

const FeedbackModal = () => {
  const context = useGeneralContext();
  const { feedbackModalOpen, setFeedbackModalOpen } = context;
  return (
    <JumbleModal
      showModal={feedbackModalOpen}
      closeModal={() => setFeedbackModalOpen(false)}
      modalSections={[() => <ContactModalContent />, () => <FeedbackModalContent />]}
    />
  );
};

const ContactModalContent = () => {
  // Get the current time in GMT+1
  const currentTime = new Date().toLocaleString('en-US', { timeZone: 'Europe/Lisbon' });

  // Convert the currentTime string to a Date object
  const currentDate = new Date(currentTime);

  // Check if it's a weekday (Monday to Friday)
  const isWeekday = currentDate.getDay() >= 1 && currentDate.getDay() <= 5;

  // Check if it's between 9 AM and 5 PM
  const isWorkingHours = currentDate.getHours() >= 9 && currentDate.getHours() < 17;

  const amOnline = isWeekday && isWorkingHours;

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-8">
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
        <div className="tw-text-2xl tw-font-bold tw-text-neutral-800">Let's Chat! 👋</div>
        <div className="tw-text-lg tw-text-neutral-700 tw-text-center tw-leading-relaxed">
          Got questions, ideas, or feedback? I'd love to hear from you! As the founder, I personally read and respond to
          every message.
        </div>
        <div className="tw-w-full tw-p-4 tw-bg-gradient-to-r tw-from-primary-50 tw-to-blue-50 tw-rounded-lg tw-border tw-border-primary-100">
          <div className="tw-flex tw-flex-row tw-items-center tw-gap-4">
            <div className="tw-w-[60px]">
              <div className="tw-relative tw-w-[60px] tw-h-[60px]">
                <img
                  className="tw-w-full tw-h-full tw-rounded-full tw-object-cover tw-shadow-md"
                  src={require('../../resources/tony-wedding.jpg')}
                  alt="User support icon"
                />
                <span
                  className={`tw-absolute tw-bottom-0 tw-right-0 tw-w-5 tw-h-5 tw-rounded-full tw-border-3 tw-border-white tw-shadow-sm ${
                    amOnline ? 'tw-bg-success-400' : 'tw-bg-neutral-500'
                  }`}
                />
              </div>
            </div>
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
              <div className="tw-text-base tw-font-semibold tw-text-neutral-800">📧 support@jumblejournal.org</div>
              <div className="tw-text-sm tw-text-success-600 tw-font-medium">Personal reply within 24 hours ⚡</div>
              <div className="tw-text-xs tw-text-neutral-500 tw-italic">Tony (Founder) responds personally</div>
            </div>
          </div>
        </div>
        <div className="tw-w-full tw-p-4 tw-bg-gradient-to-r tw-from-green-50 tw-to-emerald-50 tw-rounded-lg tw-border tw-border-green-100">
          <div className="tw-flex tw-flex-row tw-items-center tw-gap-4">
            <div className="tw-relative">
              <a
                href="https://wa.me/351913613574"
                target="_blank"
                rel="noopener noreferrer"
                className="tw-block tw-transition-transform hover:tw-scale-105"
              >
                <WhatsappIcon className="tw-w-[60px] tw-h-[60px] tw-drop-shadow-sm" />
              </a>
              {amOnline && (
                <span className="tw-absolute tw--top-1 tw--right-1 tw-w-3 tw-h-3 tw-bg-green-400 tw-rounded-full tw-animate-pulse"></span>
              )}
            </div>
            <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
              <div className="tw-text-base tw-font-semibold tw-text-neutral-800">💬 +351 913 613 574</div>
              <div className="tw-text-sm tw-text-success-600 tw-font-medium">
                {amOnline ? "I'm online now! 🟢" : 'Available 9-5, Mon-Fri (Europe/Lisbon) 🕘'}
              </div>
              <div className="tw-text-xs tw-text-neutral-500 tw-italic">Instant responses during business hours</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeedbackModalContent = () => {
  const context = useGeneralContext();
  const { setFeedbackModalOpen } = context;
  const [sending, setSending] = useState<boolean>(false);
  const [sent, setSent] = useState<boolean>(false);

  const handleSubmit = async (event: React.ChangeEvent<HTMLFormElement>) => {
    try {
      setSending(true);
      // Prevent page reload
      event.preventDefault();

      const formData = new FormData(event.target);
      const formDataObj = Object.fromEntries(formData.entries());
      const email = formDataObj.email as string;
      const subject = formDataObj.subject as string;
      const body = formDataObj.message as string;

      const result = await contactSupport({ email, subject, body });
      if (!result) {
        setSending(false);
        setSent(false);
        return;
      }
      setSending(false);
      setSent(true);
    } catch (e) {
      setSending(false);
      window.alert('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-8">
      <Form id="contact-form" className="tw-w-full" onSubmit={handleSubmit}>
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-8">
          <div className="tw-flex tw-flex-col tw-items-start tw-gap-5">
            <Mail size={32} />
            {sent ? <SuccessText /> : <FeedbackText />}
          </div>
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-6 tw-w-full">
            <Form.Group className="tw-w-full" controlId="contact-us-email">
              <Form.Label className="tw-font-semibold tw-text-base">Your Email</Form.Label>
              <Form.Control
                required
                disabled={sent}
                aria-label="contact form email"
                type="email"
                name="email"
                placeholder="email@email.com"
                size="lg"
                autoComplete="username"
                className="tw-w-full"
              />
            </Form.Group>
            <Form.Group className="tw-w-full" controlId="sign-in-password">
              <Form.Label className="tw-font-semibold tw-text-base">Subject</Form.Label>
              <Form.Control
                disabled={sent}
                required
                aria-label="contact subject"
                type="text"
                name="subject"
                size="lg"
              />
            </Form.Group>
            <Form.Group className="tw-w-full" controlId="sign-in-password">
              <Form.Label className="tw-font-semibold tw-text-base">Message</Form.Label>
              <Form.Control
                disabled={sent}
                required
                aria-label="contact message"
                as="textarea"
                rows={6}
                type="textarea"
                name="message"
                size="lg"
              />
            </Form.Group>
          </div>
          <div className="tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-3 tw-w-full">
            <button
              className="tw-w-1/2 tw-flex tw-px-4 tw-py-2 tw-justify-center tw-items-center tw-gap-2 tw-rounded tw-bg-white tw-border-2 tw-border-primary-600 tw-text-base tw-font-semibold tw-text-primary-700"
              onClick={() => {
                setFeedbackModalOpen(false);
              }}
            >
              {sent ? 'Close' : 'Cancel'}
            </button>
            <button
              disabled={sent || sending}
              className="tw-w-1/2 tw-flex tw-px-4 tw-py-2 tw-justify-center tw-items-center tw-gap-2 tw-rounded tw-bg-primary-500 tw-border-2 tw-border-primary-500 tw-text-base tw-font-semibold tw-text-white"
            >
              {sending ? (
                <span
                  className="tw-ml-1 tw-mr-1 tw-h-4 spinner-grow spinner-grow-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : sent ? (
                <span role="img" aria-label="success">
                  ✅
                </span>
              ) : (
                <Send className="tw-h-4" />
              )}
              {sent ? 'Sent' : 'Send'}
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

const FeedbackText = () => {
  return (
    <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
      <div className="tw-text-2xl tw-font-bold tw-text-primary-700">Share Your Thoughts 💬</div>
      <div className="tw-text-lg tw-text-neutral-800 tw-leading-relaxed">
        Your feedback shapes Jumble's future! 🚀 Feature ideas, bug reports, or just saying hello - I love hearing from
        you.
      </div>
      <div className="tw-text-sm tw-text-neutral-600 tw-bg-amber-50 tw-p-3 tw-rounded-lg tw-border-l-4 tw-border-amber-400">
        ⭐ <strong>Personal guarantee:</strong> Tony (founder) reads and responds to every message within 24 hours. Your
        voice matters!
      </div>
    </div>
  );
};

const SuccessText = () => {
  return (
    <div className="tw-flex tw-flex-col tw-items-start tw-gap-4">
      <div className="tw-text-2xl tw-font-bold tw-text-success-600">🎉 Message Sent Successfully!</div>
      <div className="tw-text-lg tw-text-neutral-800">
        Thank you for reaching out! 🙏 Your message is already in my inbox.
      </div>
      <div className="tw-text-base tw-bg-success-50 tw-p-3 tw-rounded-lg tw-border tw-border-success-200">
        ⏰ <strong>What's next?</strong> I'll personally review your message and respond within 24 hours. Keep an eye on
        your email!
      </div>
    </div>
  );
};

export default FeedbackModal;
